// Browser-measured wait until the complete reply is ready for display, including
// network time and server retries/fallbacks. This does not measure browser paint.
export function startChatbotRequest(
  { stage, requestType, userMessageIndex, requestedModel, chatMode },
  {
    now = () => performance.now(),
    timestamp = () => new Date().toISOString(),
    createId = () => crypto.randomUUID(),
  } = {}
) {
  const started = now();
  const request = {
    requestId: createId(),
    requestType,
    userMessageIndex,
    requestedModel,
    chatMode,
    startedAt: timestamp(),
    finishedAt: null,
    latencyMs: null,
    status: "pending",
    resolvedModel: null,
    fallbackModel: null,
    error: null,
  };

  function persist(value) {
    const saved = stage.get("chatbotRequests");
    const requests = Array.isArray(saved) ? saved : [];
    const index = requests.findIndex((entry) => entry.requestId === request.requestId);
    stage.set(
      "chatbotRequests",
      index < 0
        ? [...requests, value]
        : requests.map((entry, i) => (i === index ? value : entry))
    );
  }

  persist(request);
  let finished = false;
  return {
    requestId: request.requestId,
    finish({ status, resolvedModel = null, fallbackModel = null, error = null }) {
      if (finished) return;
      finished = true;
      persist({
        ...request,
        finishedAt: timestamp(),
        latencyMs: Math.max(0, Math.round(now() - started)),
        status,
        resolvedModel,
        fallbackModel,
        error,
      });
    },
  };
}

export function summarizeChatbotRequests(requests = []) {
  const completed = requests.filter((request) => request.status === "completed");
  const latencies = completed
    .map((request) => request.latencyMs)
    .filter((value) => typeof value === "number" && Number.isFinite(value) && value >= 0);
  return {
    chatbotRequestCount: requests.length,
    chatbotCompletedRequestCount: completed.length,
    chatbotFailedRequestCount: requests.filter((request) => request.status === "failed").length,
    // Pending means no completion was recorded, e.g. the browser closed mid-request.
    chatbotPendingRequestCount: requests.filter((request) => request.status === "pending").length,
    chatbotMeanLatencyMs: latencies.length
      ? Math.round(latencies.reduce((sum, value) => sum + value, 0) / latencies.length)
      : "",
    chatbotMaxLatencyMs: latencies.length ? Math.max(...latencies) : "",
  };
}
